import React, { useState, useEffect } from 'react';
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Typography,
  useTheme,
  IconButton,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { AttachFile as AttachFileIcon, Clear as ClearIcon } from '@mui/icons-material';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditTopic = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [course, setCourse] = useState('');
  const [module, setModule] = useState('');
  const [topicName, setTopicName] = useState('');
  const [topicContent, setTopicContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [courseMap, setCourseMap] = useState({});
  const [moduleMap, setModuleMap] = useState({});
  const [loading, setLoading] = useState(true);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB in bytes
  const { selectedBranchId } = useBranch();
  const [originalTopic, setOriginalTopic] = useState(null);

  const getOrganizationId = () => {
    try {
      const raw = sessionStorage.getItem('user') || localStorage.getItem('user') || '{}';
      const user = JSON.parse(raw) || {};
      return user.organization_id ?? null;
    } catch {
      return null;
    }
  };

  const buildOrgBranchQuery = (organizationId, branchId) => {
    const params = new URLSearchParams();
    if (organizationId !== null && organizationId !== undefined && organizationId !== '') {
      params.append('organization', organizationId);
    }
    if (branchId !== null && branchId !== undefined && branchId !== '') {
      params.append('branch', branchId);
    }
    const query = params.toString();
    return query ? `?${query}` : '';
  };

  useEffect(() => {
    const fetchAndLoadData = async () => {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      try {
        // 1. Courses
               // 1. Courses
        const orgBranchQuery = buildOrgBranchQuery(getOrganizationId(), selectedBranchId);
        const coursesRes = await axios.get(`${API_BASE_URL}/api/course/courses/${orgBranchQuery}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const loadedCourses = Array.isArray(coursesRes.data) ? coursesRes.data : [];
        setCourses(loadedCourses);

        const cMap = {};
        loadedCourses.forEach((c) => {
          if (c.id && c.course_name) cMap[c.id] = c.course_name;
        });
        setCourseMap(cMap);

        // 2. Modules
            
              
        const modulesRes = await axios.get(`${API_BASE_URL}/api/course/modules/${orgBranchQuery}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const loadedModules = Array.isArray(modulesRes.data) ? modulesRes.data : [];
        setModules(loadedModules);

        const mMap = {};
        loadedModules.forEach((m) => {
          if (m.id && m.module_name) mMap[m.id] = m.module_name;
        });
        setModuleMap(mMap);

        // 3. Load the specific topic to edit
        const topicRes = await axios.get(
          `${API_BASE_URL}/api/course/topics/${id}/`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        const topic = topicRes.data;

        if (topic && topic.id) {
          setCourse(topic.course || '');
          setModule(topic.module || '');
          setTopicName(topic.topic_name || '');
          setTopicContent(topic.topic_content || '');
          setOriginalTopic(topic);
          // Optional: show current file name if exists
          if (topic.pdf_file) {
            setFileName(topic.pdf_file.split('/').pop()); // just filename
          }
        } else {
          setSnackbarMessage('Topic not found');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }

      } catch (err) {
        console.error('Failed to load edit data:', err);
        setSnackbarMessage('Failed to load topic or related data');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

      fetchAndLoadData();
  }, [id, selectedBranchId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        setSnackbarMessage('File size exceeds 1MB limit. Please choose a smaller file.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        // Clear the file input
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!course || !module || !topicName.trim() || !topicContent.trim()) {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');

      // Create FormData
      const formDataToSend = new FormData();
            formDataToSend.append('course', parseInt(course));
      formDataToSend.append('module', parseInt(module));
      formDataToSend.append('topic_name', topicName.trim());
      formDataToSend.append('topic_content', topicContent.trim());
      formDataToSend.append(
        'organization',
        originalTopic?.organization ?? getOrganizationId() ?? ''
      );
      formDataToSend.append(
        'branch',
        originalTopic?.branch ?? selectedBranchId ?? ''
      );

      // Add PDF file only if selected
      if (selectedFile) {
        formDataToSend.append('pdf_file', selectedFile);
      }

      // Send PUT request with FormData
      await axios.put(
        `${API_BASE_URL}/api/course/topics/${id}/`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      // Success
      setShowSuccessModal(true);
      setSnackbarMessage('Topic updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

    } catch (err) {
      console.error('Update failed:', err);
      setSnackbarMessage(
        err.response?.data?.detail ||
        err.response?.data?.pdf_file?.[0] ||  // show file-specific error if any
        'Failed to update topic. Please try again.'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Topic Details
          </Typography>
        </Box>
        <form onSubmit={handleSave}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 400,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '&.MuiInputLabel-shrink': {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  padding: '0 4px',
                }
              }}
            >Course Name *</InputLabel>
            <Select
              value={course}
              label="Course Name *"
              onChange={(e) => {
                setCourse(e.target.value);
                setModule(''); // reset module
              }}
              disabled={loading}
              sx={{ /* your existing sx */ }}
              required
            >
              {loading ? (
                <MenuItem disabled>Loading courses...</MenuItem>
              ) : (
                courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.course_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 400,
                '&.Mui-focused': {
                  color: theme.palette.primary.main,
                },
                '&.MuiInputLabel-shrink': {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  padding: '0 4px',
                }
              }}
            >Module Name *</InputLabel>
            <Select
              value={module}
              label="Module Name *"
              onChange={(e) => setModule(e.target.value)}
              disabled={!course || loading}
              sx={{ /* your existing sx */ }}
              required
            >
              {loading ? (
                <MenuItem disabled>Loading modules...</MenuItem>
              ) : (
                modules
                  .filter((m) => m.course === parseInt(course))
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.module_name}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>

          <TextField
            label="Topic Name *"
            variant="outlined"
            fullWidth
            value={topicName}
            onChange={(e) => setTopicName(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />

          <TextField
            label="Topic Content *"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={topicContent}
            onChange={(e) => setTopicContent(e.target.value)}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                color: theme.palette.text.primary,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
            required
          />

          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<AttachFileIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              }}
            >
              Choose PDF
              <input
                type="file"
                hidden
                accept=".pdf"
                onChange={handleFileChange}
              />
            </Button>
            {fileName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{fileName}</Typography>
                <IconButton onClick={() => { setSelectedFile(null); setFileName(''); }} size="small">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.primary, fontWeight: 500, ml: 1, display: 'block', mb: 3 }}>
            Maximum file size: 1MB
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Tooltip
              title={
                !course && "Select Course" ||
                !module && "Select Module" ||
                !topicName && "Enter Topic" ||
                !topicContent && "Enter Topic Content"
              }
              arrow
            >
              <span>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!topicName || !course || !module || !topicContent}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  UPDATE
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancel}
            >
              CANCEL
            </Button>
          </Box>
        </form>

        <Modal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 400,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: 24,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
              <Typography variant="h6" sx={{ color: theme.palette.primary.contrastText }}>
                Topic Updated ✅
              </Typography>
            </Box>
            <Box sx={{ padding: '20px' }}>
              <Typography sx={{ mb: 2 }}>
                Topic details have been updated successfully.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  OK
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditTopic;